import { UTCDate } from '@date-fns/utc';
import { Currency } from '@seapoint/server/enums';
import { endOfDay, isWithinInterval } from 'date-fns';
import { AccountView, TransactionView } from '../../../types/entity-types';
import { SupportedCountryCurrency } from '../../../types/supported-country-currency';
import { getAmountWithCurrency } from '../../../utils/get-amount-with-currency';
import { MoneyMovementRange } from '../components/charts/money-movement-filter';
import { isInternalTransfer } from './is-internal-transfer';
import { isRefund } from './is-refund';

export type TopMoneySource = {
  id: string;
  name: string;
  amount: number; // absolute sum in base currency
  percent: number; // percentage share in [0, 100]
  avatarUrl?: string | null;
};

type GetTopMoneySourcesParams = {
  accounts: AccountView[];
  topN?: number;
  startDate?: Date;
  endDate?: Date;
};

type GetPreviousSourcesParams = GetTopMoneySourcesParams & {
  range?: MoneyMovementRange;
  baseCurrency: SupportedCountryCurrency | Currency;
};

const toAbsolute = (value: number | undefined | null): number => {
  if (!value) return 0;
  return Math.abs(value);
};

// Helper to generate dynamic tooltip text
const getTooltipText = (range?: GetPreviousSourcesParams['range']): string => {
  switch (range) {
    case 'current_month':
      return 'vs. previous month';
    case 'last_month':
      return 'vs. previous month';
    case 'last_3_months':
      return 'vs. previous 3 months';
    case 'last_6_months':
      return 'vs. previous 6 months';
    case 'last_year':
      return 'vs. previous year';
    default:
      return 'vs. previous period';
  }
};

// Prefer vendor identifiers when available; fallback to payee or beneficiary information
const getCounterpartyKeyAndName = (txn: TransactionView): { key: string; name: string; avatarUrl?: string | null } => {
  const vendor = txn.vendor;
  if (vendor) {
    const id = vendor.id;
    return {
      key: id,
      name: vendor.name ?? 'Unknown',
      avatarUrl: (vendor as unknown as { logo?: string }).logo as string | undefined,
    };
  }

  // If vendor doesn't exist, check payee information
  const payee = txn.payee;
  if (payee?.name) {
    const payeeKey = payee.iban || payee.accountNumber || `payee:${payee.name}`;
    return { key: payeeKey, name: payee.name };
  }

  // If we have a beneficiary (typically for outgoing), try to use that name
  const beneficiary = txn.beneficiary;
  if (beneficiary?.name) {
    return { key: `beneficiary:${beneficiary.name}`, name: beneficiary.name };
  }

  // Fallback: Unknown counterparty
  return { key: `unknown:${txn.id}`, name: 'Unknown' };
};

type GetTopSourcesParams = GetTopMoneySourcesParams & {
  direction: 'in' | 'out';
};

export const getTopSources = ({ accounts, startDate, endDate, direction }: GetTopSourcesParams): TopMoneySource[] => {
  if (!accounts || accounts.length === 0) return [];
  const hasRange = Boolean(startDate && endDate);
  const adjustedEnd = endDate ? endOfDay(endDate) : undefined;

  const sums = new Map<string, { name: string; amount: number; avatarUrl?: string | null }>();

  for (const account of accounts) {
    for (const txn of account.transactions || []) {
      if (!txn) continue;
      if (isInternalTransfer(txn)) continue;

      const isCredit = Boolean(txn.isCredit);
      const isIncoming = direction === 'in';

      // Skip if transaction doesn't match the direction we're looking for
      if (isIncoming && !isCredit) continue;
      if (!isIncoming && isCredit) continue;

      if (hasRange) {
        if (!txn.fspDate) continue;
        const txDate = new UTCDate(txn.fspDate);
        if (!isWithinInterval(txDate, { start: startDate as Date, end: adjustedEnd as Date })) continue;
      }

      const { key, name, avatarUrl } = getCounterpartyKeyAndName(txn);
      const prev = sums.get(key)?.amount ?? 0;

      // For regular transactions, add the amount
      // For refunds, subtract the amount to net out the vendor's impact
      const amount = toAbsolute(txn.baseCurrencyAmount);
      const isRefundTransaction = isRefund(txn.type);
      const adjustedAmount = isRefundTransaction ? -amount : amount;

      sums.set(key, { name, amount: prev + adjustedAmount, avatarUrl });
    }
  }

  // Filter out vendors with zero or negative amounts (fully netted out by refunds)
  const validVendors = Array.from(sums.entries()).filter(([, v]) => v.amount > 0);

  const total = validVendors.reduce((sum, [, v]) => sum + v.amount, 0);
  const items: TopMoneySource[] = validVendors.map(([key, v]) => ({
    id: key,
    name: v.name,
    amount: v.amount,
    percent: total > 0 ? (v.amount / total) * 100 : 0,
    avatarUrl: v.avatarUrl,
  }));

  items.sort((a, b) => b.amount - a.amount);
  return items;
};

export const getTopIncomingSources = ({ accounts, startDate, endDate }: GetTopMoneySourcesParams): TopMoneySource[] => {
  return getTopSources({ accounts, startDate, endDate, direction: 'in' });
};

export const getTopOutgoingSources = ({ accounts, startDate, endDate }: GetTopMoneySourcesParams): TopMoneySource[] => {
  return getTopSources({ accounts, startDate, endDate, direction: 'out' });
};

export const getPreviousTopIncomingSources = ({
  accounts,
  baseCurrency,
  startDate,
  endDate,
  range,
}: GetPreviousSourcesParams) => {
  if (!startDate || !endDate) return null;

  const duration = endDate.getTime() - startDate.getTime();
  const prevStart = new UTCDate(startDate.getTime() - duration);
  const prevEnd = new UTCDate(startDate);

  const currentTotal = getTopSources({ accounts, startDate, endDate, direction: 'in' }).reduce(
    (sum, item) => sum + item.amount,
    0,
  );
  const previousTotal = getTopSources({
    accounts,
    startDate: prevStart,
    endDate: prevEnd,
    direction: 'in',
  }).reduce((sum, item) => sum + item.amount, 0);

  const change = previousTotal > 0 ? ((currentTotal - previousTotal) / previousTotal) * 100 : 0;
  const trend = change > 0 ? 'up' : change < 0 ? 'down' : 'neutral';

  const trendValueClass =
    trend === 'up' ? 'text-seapoint-green' : trend === 'down' ? 'text-seapoint-red' : 'text-seapoint-gray-text';

  return {
    value: getAmountWithCurrency({ amount: currentTotal, currency: baseCurrency, isAccountBalance: true }),
    percent: `${Math.abs(change).toFixed(1)}%`,
    trend: trend as 'up' | 'down' | 'neutral',
    trendValueClass: trendValueClass as 'text-seapoint-green' | 'text-seapoint-red' | 'text-seapoint-gray-text',
    tooltipText: getTooltipText(range),
  };
};

export const getPreviousTopOutgoingSources = ({
  accounts,
  baseCurrency,
  topN = 5,
  startDate,
  endDate,
  range,
}: GetPreviousSourcesParams) => {
  if (!startDate || !endDate) return null;

  // Calculate previous period (shift by same duration)
  const duration = endDate.getTime() - startDate.getTime();
  const prevStart = new UTCDate(startDate.getTime() - duration);
  const prevEnd = new UTCDate(startDate);

  const currentTotal = getTopSources({ accounts, topN, startDate, endDate, direction: 'out' }).reduce(
    (sum, item) => sum + item.amount,
    0,
  );
  const previousTotal = getTopSources({
    accounts,
    topN,
    startDate: prevStart,
    endDate: prevEnd,
    direction: 'out',
  }).reduce((sum, item) => sum + item.amount, 0);

  const change = previousTotal > 0 ? ((currentTotal - previousTotal) / previousTotal) * 100 : 0;
  const trend = change > 0 ? 'up' : change < 0 ? 'down' : 'neutral';

  const trendValueClass =
    trend === 'up' ? 'text-seapoint-red' : trend === 'down' ? 'text-seapoint-green' : 'text-seapoint-gray-text';

  return {
    value: getAmountWithCurrency({ amount: currentTotal, currency: baseCurrency, isAccountBalance: true }),
    percent: `${Math.abs(change).toFixed(1)}%`,
    trend: trend as 'up' | 'down' | 'neutral',
    trendValueClass: trendValueClass as 'text-seapoint-green' | 'text-seapoint-red' | 'text-seapoint-gray-text',
    tooltipText: getTooltipText(range),
  };
};

export const getTopMoneyMovementSources = ({
  accounts,
  baseCurrency,
  topN = 5,
  startDate,
  endDate,
  range,
}: GetPreviousSourcesParams) => ({
  topIn: getTopIncomingSources({ accounts, topN, startDate, endDate }),
  topOut: getTopOutgoingSources({ accounts, topN, startDate, endDate }),
  comparison: {
    topIn: getPreviousTopIncomingSources({ accounts, baseCurrency, topN, startDate, endDate, range }),
    topOut: getPreviousTopOutgoingSources({ accounts, baseCurrency, topN, startDate, endDate, range }),
  },
});
